import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import Lenis from 'lenis'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const SmoothScrollContext = createContext(null)

export const useSmoothScroll = () => useContext(SmoothScrollContext)

export function SmoothScrollProvider({ children }) {
  const lenisRef = useRef(null)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [enabled, setEnabled] = useState(false)

  const scrollTo = useCallback((target, options = {}) => {
    const lenis = lenisRef.current
    if (lenis) {
      lenis.scrollTo(target, {
        offset: options.offset ?? -88,
        duration: options.duration ?? 1.2,
      })
      return
    }

    const el = typeof target === 'string' ? document.querySelector(target) : target
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const refresh = useCallback(() => {
    lenisRef.current?.resize()
    ScrollTrigger.refresh()
  }, [])

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) {
      setEnabled(false)
      return undefined
    }

    const lenis = new Lenis({
      autoRaf: true,
      duration: 1.1,
      smoothWheel: true,
      touchMultiplier: 1.2,
      wheelMultiplier: 1,
    })

    lenisRef.current = lenis
    setEnabled(true)

    const onScroll = (instance) => {
      setScrollProgress(instance.progress)
      ScrollTrigger.update()
    }

    lenis.on('scroll', onScroll)

    ScrollTrigger.scrollerProxy(document.documentElement, {
      scrollTop(value) {
        if (arguments.length) {
          lenis.scrollTo(value, { immediate: true })
        }
        return lenis.scroll
      },
      getBoundingClientRect() {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        }
      },
      pinType: document.documentElement.style.transform ? 'transform' : 'fixed',
    })

    const onRefresh = () => lenis.resize()
    ScrollTrigger.addEventListener('refresh', onRefresh)

    const onResize = () => {
      lenis.resize()
      ScrollTrigger.refresh()
    }
    window.addEventListener('resize', onResize)

    const refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 600)

    return () => {
      clearTimeout(refreshTimer)
      window.removeEventListener('resize', onResize)
      ScrollTrigger.removeEventListener('refresh', onRefresh)
      ScrollTrigger.scrollerProxy(document.documentElement, {})
      lenis.off('scroll', onScroll)
      lenis.destroy()
      lenisRef.current = null
    }
  }, [])

  return (
    <SmoothScrollContext.Provider value={{ scrollTo, scrollProgress, enabled, refresh }}>
      {children}
    </SmoothScrollContext.Provider>
  )
}
